// @ts-nocheck
/**
 * VoterStatsStrip — filter-aware cohort totals banner.
 *
 * Sits above the Voters table and shows totals / tier counts for the
 * currently-filtered cohort by calling GET /voters/stats with the same
 * filter + search query params the list endpoint accepts. When no
 * filters are active it falls back to the tenant-wide totals (same
 * behavior LiveBackendStatus surfaces today).
 *
 * Visually matches LiveBackendStatus: slate-800 card, slate-700 border,
 * tiny uppercase labels + large values on a slate-900/60 stat-card grid.
 *
 * Refetches on filter/search change with a 300ms debounce; stale
 * requests are aborted via AbortController so the UI only ever settles
 * on the latest cohort.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import client from '@/api/client';

type VoterStatsResponse = {
  total_voters: number;
  active_voters: number;
  average_score: number;
  by_tier: Record<string, number>;
  by_party: Record<string, number>;
  primary_voters: number;
  high_scorers: number;
};

export interface VoterStatsStripProps {
  filters: Record<string, unknown>;
  search: string;
  countyTotal?: number;
}

/**
 * Build the query params object for GET /voters/stats using the same
 * rules as the list endpoint: spread filters, add `q` for search,
 * convert array filter values to `<key>__in=a,b,c`.
 */
function buildParams(
  filters: Record<string, unknown>,
  search: string,
): Record<string, string | number | boolean> {
  const params: Record<string, string | number | boolean> = {};
  if (search && search.trim() !== '') {
    params.q = search.trim();
  }
  Object.entries(filters ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (Array.isArray(value)) {
      if (value.length === 0) return;
      params[`${key}__in`] = value.join(',');
    } else {
      params[key] = value as string | number | boolean;
    }
  });
  return params;
}

function hasActiveFilter(
  filters: Record<string, unknown>,
  search: string,
): boolean {
  if (search && search.trim() !== '') return true;
  return Object.values(filters ?? {}).some((v) => {
    if (v === null || v === undefined || v === '') return false;
    if (Array.isArray(v)) return v.length > 0;
    return true;
  });
}

export function VoterStatsStrip({
  filters,
  search,
  countyTotal,
}: VoterStatsStripProps) {
  const [stats, setStats] = useState<VoterStatsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  // Stable stringification so the effect only fires on a real change,
  // not on every parent re-render that passes a fresh object identity.
  const paramsKey = useMemo(
    () => JSON.stringify(buildParams(filters, search)),
    [filters, search],
  );

  // Keep the latest abort controller so prior in-flight requests get
  // cancelled when filters change again before the server responds.
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const debounceId = setTimeout(() => {
      // Cancel any previous in-flight request.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const params = JSON.parse(paramsKey) as Record<string, string | number | boolean>;

      setLoading(true);
      setError('');
      client
        .get<VoterStatsResponse>('/voters/stats', {
          params,
          signal: controller.signal,
        })
        .then((res) => {
          setStats(res.data);
          setLoading(false);
        })
        .catch((err: any) => {
          // Ignore aborted-by-newer-request errors — a fresher request
          // is already in flight and will settle the UI.
          if (
            err?.name === 'CanceledError' ||
            err?.code === 'ERR_CANCELED' ||
            err?.message === 'canceled'
          ) {
            return;
          }
          const code = err?.response?.status;
          const detail = err?.response?.data?.detail ?? err?.message ?? 'Unknown error';
          setError(code ? `Stats error ${code}: ${detail}` : `Stats error: ${detail}`);
          setLoading(false);
        });
    }, 300);

    return () => {
      clearTimeout(debounceId);
    };
  }, [paramsKey]);

  const filtered = hasActiveFilter(filters, search);
  const caption = filtered
    ? 'Stats for current filter'
    : countyTotal !== undefined
      ? `Stats — unfiltered (${countyTotal.toLocaleString()} voters)`
      : 'Stats — unfiltered';

  return (
    <div className="mb-4 rounded-lg border border-border-base bg-bg-raised/60 px-4 py-3 shadow-lg">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-wider text-fg-muted">
          {caption}
        </span>
        {error && (
          <span className="text-xs text-fg-muted">&nbsp;</span>
        )}
      </div>

      {error ? (
        <div className="mt-3 rounded border border-status-danger-bg/60 bg-status-danger-bg/30 px-3 py-2 text-xs text-status-danger">
          {error}
        </div>
      ) : loading && !stats ? (
        <SkeletonRow />
      ) : stats ? (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
          <Stat
            label="Total in filter"
            value={stats.total_voters.toLocaleString()}
            subtitle={
              countyTotal !== undefined
                ? `of ${countyTotal.toLocaleString()}`
                : undefined
            }
            highlight
          />
          <Stat label="Avg score" value={stats.average_score.toFixed(1)} />
          <Stat
            label="Super voters"
            value={(stats.by_tier?.SUPER_VOTER ?? 0).toLocaleString()}
          />
          <Stat label="Hot" value={(stats.by_tier?.HOT ?? 0).toLocaleString()} />
          <Stat label="Warm" value={(stats.by_tier?.WARM ?? 0).toLocaleString()} />
          <Stat label="Cool" value={(stats.by_tier?.COOL ?? 0).toLocaleString()} />
        </div>
      ) : (
        <SkeletonRow />
      )}
    </div>
  );
}

function Stat({
  label,
  value,
  subtitle,
  highlight = false,
}: {
  label: string;
  value: string;
  subtitle?: string;
  highlight?: boolean;
}) {
  return (
    <div className="rounded bg-bg-base/60 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-fg-muted">
        {label}
      </div>
      <div
        className={`mt-0.5 font-semibold tabular-nums ${
          highlight ? 'text-accent-fg text-lg' : 'text-fg-primary'
        }`}
      >
        {value}
      </div>
      {subtitle && (
        <div className="mt-0.5 text-[10px] text-fg-muted">{subtitle}</div>
      )}
    </div>
  );
}

function SkeletonRow() {
  return (
    <div
      className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm"
      aria-busy="true"
      aria-label="Loading stats"
    >
      {Array.from({ length: 6 }).map((_, i) => (
        <div
          key={i}
          className="rounded bg-bg-base/60 px-3 py-2 animate-pulse"
        >
          <div className="h-2 w-16 rounded bg-bg-elevated/70" />
          <div className="mt-2 h-4 w-20 rounded bg-bg-elevated/70" />
        </div>
      ))}
    </div>
  );
}

export default VoterStatsStrip;
